/**
 * Issuer links — issuers with no Cynapse link, and the two repairs for them.
 *
 * An unlinked issuer parks its director trades and holder filings as
 * `no_issuer`. Each row offers the repairs that fit it: take the link from a
 * sibling row of the same company (rebind), or take the issuer's name off the
 * Cynapse record wrongly carrying it and give it its own (split, fund-shape
 * types). Apply is enabled only after a dry run of that same repair, and the
 * dry run shows exactly what would move — the operator decides on evidence.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Link2, Eye, Play, Loader2, RefreshCw } from 'lucide-react';
import { TableScroll } from './ui/table-scroll.js';
import {
  getIssuerLinkCandidates,
  runIssuerLinkRepair,
  type IssuerLinkCandidate,
  type IssuerLinkRepair,
  type IssuerLinkRepairOutcome,
} from '../lib/api/issuer-links.js';

const keyOf = (r: IssuerLinkRepair) =>
  r.kind === 'split' ? `split:${r.entity_id}` : `${r.kind}:${r.entity_id}:${r.from_entity_id}`;

interface ActionState {
  running?: 'dry' | 'apply';
  dry?: IssuerLinkRepairOutcome;
  applied?: IssuerLinkRepairOutcome;
  error?: string;
}

export function IssuerLinkRepairPanel() {
  const [rows, setRows] = useState<IssuerLinkCandidate[] | null>(null);
  const [includeUnparked, setIncludeUnparked] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actions, setActions] = useState<Record<string, ActionState>>({});
  // Per merge: mention ids the operator ticks to detach before the merge.
  const [detach, setDetach] = useState<Record<string, string[]>>({});
  const toggleDetach = (k: string, ids: string[], on: boolean) =>
    setDetach(d => {
      const cur = new Set(d[k] ?? []);
      ids.forEach(id => (on ? cur.add(id) : cur.delete(id)));
      return { ...d, [k]: Array.from(cur) };
    });
  // A merge applies with the mentions ticked on its dry run.
  const withDetach = (r: IssuerLinkRepair): IssuerLinkRepair =>
    r.kind === 'rebind' && (detach[keyOf(r)] ?? []).length > 0 ? { ...r, detach_mentions: detach[keyOf(r)] } : r;
  const picker = (r: IssuerLinkRepair) => {
    const k = keyOf(r);
    const groups = actions[k]?.dry?.sibling_mentions;
    if (r.kind !== 'rebind' || !groups || groups.length === 0 || actions[k]?.applied?.applied) return null;
    const chosen = new Set(detach[k] ?? []);
    return (
      <div className="mt-2 rounded border border-grey-100 px-3 py-2 text-xs text-grey-700">
        <div className="font-medium text-grey-800 mb-1">
          Mentions that move with the merge. Tick any that name another company; they are unlinked first (old values kept).
        </div>
        {groups.map(g => {
          const on = g.mention_ids.every(id => chosen.has(id));
          return (
            <label key={g.company_name} className="flex items-center gap-2">
              <input type="checkbox" checked={on} onChange={e => toggleDetach(k, g.mention_ids, e.target.checked)} />
              <span>{g.company_name}</span>
              <span className="text-grey-400">× {g.mention_ids.length}</span>
            </label>
          );
        })}
      </div>
    );
  };
  // Applied repairs, kept here: an applied issuer leaves the list on refresh,
  // and its result (what moved, what re-resolved) must stay readable.
  const [appliedLog, setAppliedLog] = useState<{ issuer: string; o: IssuerLinkRepairOutcome }[]>([]);
  const busy = Object.values(actions).some(a => a.running);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setRows(await getIssuerLinkCandidates(includeUnparked));
    } catch (err: any) {
      setLoadError(err?.response?.data?.detail || err?.message || 'Could not load issuers');
    }
  }, [includeUnparked]);

  useEffect(() => { load(); }, [load]);

  const run = async (repair: IssuerLinkRepair, mode: 'dry' | 'apply', label: string, issuer: string) => {
    const k = keyOf(repair);
    if (mode === 'apply' && !window.confirm(`${label}?\n\nThis writes to Assayer and Cynapse. The dry run above shows what will change.`)) return;
    setActions(a => ({ ...a, [k]: { ...a[k], running: mode, error: undefined } }));
    try {
      const res = await runIssuerLinkRepair([repair], mode === 'dry');
      const out = res.results[0];
      setActions(a => ({ ...a, [k]: { ...a[k], running: undefined, [mode === 'dry' ? 'dry' : 'applied']: out } }));
      if (mode === 'apply' && out?.applied) {
        setAppliedLog(l => [{ issuer, o: out }, ...l]);
        load();
      }
    } catch (err: any) {
      setActions(a => ({ ...a, [k]: { ...a[k], running: undefined, error: err?.response?.data?.detail || err?.message || 'Request failed' } }));
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Link2 className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Issuer links</h3>
            <p className="text-sm text-grey-600 mt-1">
              Issuers with no Cynapse record linked. Their director trades and holder filings are parked until
              they have one. <strong>Take link</strong> moves the record from a sibling row of the <em>same</em>{' '}
              company (the sibling is retired and anything on it moves across) — check the sibling really is the same
              company: a shared ticker on another exchange can be someone else. <strong>Split</strong> is for a record
              wrongly filed under another company&apos;s name: Cynapse takes the name off that record and creates this
              issuer&apos;s own — funds and listed companies; a company split applies exactly the records its dry run
              listed. Every repair dry-runs first; an applied repair re-resolves the parked rows (only those parked —
              notices already resolved against a wrong record are not redone).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 sm:ml-4 flex-shrink-0">
          <label className="flex items-center gap-1.5 text-xs text-grey-600">
            <input type="checkbox" checked={includeUnparked} onChange={e => setIncludeUnparked(e.target.checked)} />
            Include issuers with nothing parked
          </label>
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {appliedLog.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-grey-500">Applied this session</h4>
          {appliedLog.map((a, i) => (
            <div key={i}>
              <div className="text-sm text-grey-800">{a.issuer}</div>
              <Outcome label="Applied" o={a.o} />
            </div>
          ))}
        </div>
      )}

      {loadError && <p className="mt-4 text-sm text-red-700">{loadError}</p>}
      {!rows && !loadError && <p className="mt-4 text-sm text-grey-500">Loading issuers…</p>}
      {rows && rows.length === 0 && <p className="mt-4 text-sm text-grey-500">No unlinked issuers{includeUnparked ? '' : ' with parked rows'}.</p>}

      {rows && rows.length > 0 && (
        <TableScroll className="mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-grey-100 text-left text-xs text-grey-500">
                <th className="py-2 pr-4">Issuer</th>
                <th className="py-2 pr-4">Parked</th>
                <th className="py-2 pr-4">Filings</th>
                <th className="py-2 pr-4">Repair</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.entity_id} className="border-b border-grey-50 align-top">
                  <td className="py-3 pr-4 min-w-[12rem]">
                    <Link to={`/entities/${c.entity_id}`} className="font-medium text-grey-900 hover:text-primary-700 hover:underline">
                      {c.canonical_identifier ? `${c.canonical_identifier} · ` : ''}{c.display_name}
                    </Link>
                    <div className="text-xs text-grey-400">{c.entity_type} · sync {c.cynapse_sync_status ?? '—'}</div>
                  </td>
                  <td className="py-3 pr-4 text-grey-700">{c.parked}</td>
                  <td className="py-3 pr-4 text-grey-700">{c.announcements}</td>
                  <td className="py-3 pr-4 space-y-3 min-w-[22rem]">
                    {c.siblings.map(s => {
                      const repair: IssuerLinkRepair = { kind: 'rebind', entity_id: c.entity_id, from_entity_id: s.entity_id };
                      return (
                        <RepairRow
                          key={s.entity_id}
                          title={<>Take link from <Link to={`/entities/${s.entity_id}`} className="font-medium hover:underline">{s.canonical_identifier ? `${s.canonical_identifier} · ` : ''}{s.display_name}</Link></>}
                          detail={`${s.entity_type} · matched on ${s.match} · ${s.announcements} filing${s.announcements === 1 ? '' : 's'} on it`}
                          state={actions[keyOf(repair)]}
                          busy={busy}
                          onRun={m => run(withDetach(repair), m, `Move the Cynapse link from ${s.display_name} onto ${c.display_name} and retire ${s.display_name}`, c.display_name)}
                          extra={picker(repair)}
                        />
                      );
                    })}
                    {c.can_split && (() => {
                      const repair: IssuerLinkRepair = { kind: 'split', entity_id: c.entity_id };
                      const st = actions[keyOf(repair)];
                      const dry = st?.dry;
                      // A company split applies exactly the records its dry run found.
                      const applyRepair: IssuerLinkRepair = dry?.path === 'company'
                        ? { ...repair, remove_from: dry.cynapse?.remove_from ?? [] }
                        : repair;
                      // The name sits on this issuer's OWN record: offer its link instead.
                      const ownRecordOwners = dry?.error === 'alias_holder_is_this_company'
                        ? (dry.cynapse?.holders ?? [])
                            .map(h => dry.owners?.[h.entity_id])
                            .filter((o): o is NonNullable<typeof o> => !!o && o.entity_id !== c.entity_id)
                        : [];
                      return (
                        <>
                          <RepairRow
                            title={<>Split — give {c.display_name} its own record</>}
                            detail="Takes the name off any Cynapse record wrongly carrying it"
                            state={st}
                            busy={busy}
                            onRun={m => run(m === 'apply' ? applyRepair : repair, m, `Take "${c.display_name}" off the records carrying it and create its own record`, c.display_name)}
                          />
                          {ownRecordOwners.map(o => {
                            const ownerLink = <Link to={`/entities/${o.entity_id}`} className="font-medium hover:underline">{o.canonical_identifier ? `${o.canonical_identifier} · ` : ''}{o.display_name}</Link>;
                            if (o.same_name) {
                              // Same company under its own name: merge the rows.
                              const rb: IssuerLinkRepair = { kind: 'rebind', entity_id: c.entity_id, from_entity_id: o.entity_id };
                              return (
                                <RepairRow
                                  key={o.entity_id}
                                  title={<>Take link from {ownerLink}</>}
                                  detail={`${o.entity_type} · same name — owns ${c.display_name}'s own Cynapse record; it is retired and its rows move here`}
                                  state={actions[keyOf(rb)]}
                                  busy={busy}
                                  onRun={m => run(withDetach(rb), m, `Move the Cynapse link from ${o.display_name} onto ${c.display_name} and retire ${o.display_name}`, c.display_name)}
                                  extra={picker(rb)}
                                />
                              );
                            }
                            // A different entity holds the issuer's record: move only the link —
                            // or, when its rows are really the issuer's (a misnamed stub), merge it.
                            const tr: IssuerLinkRepair = { kind: 'transfer', entity_id: c.entity_id, from_entity_id: o.entity_id };
                            const mg: IssuerLinkRepair = { kind: 'rebind', entity_id: c.entity_id, from_entity_id: o.entity_id };
                            return (
                              <div key={o.entity_id} className="space-y-3">
                                <RepairRow
                                  title={<>Move link here from {ownerLink}</>}
                                  detail={`${o.entity_type} · a different entity holding ${c.display_name}'s own record — it keeps its rows, loses the link, and then needs its own split`}
                                  state={actions[keyOf(tr)]}
                                  busy={busy}
                                  onRun={m => run(tr, m, `Move the Cynapse link from ${o.display_name} onto ${c.display_name}? ${o.display_name} keeps its rows and is left unlinked`, c.display_name)}
                                />
                                <RepairRow
                                  title={<>Merge {ownerLink} into {c.display_name}</>}
                                  detail={`Only if ${o.display_name} is really ${c.display_name} under the wrong name — check its mentions on the dry run; it is retired and its rows move here`}
                                  state={actions[keyOf(mg)]}
                                  busy={busy}
                                  onRun={m => run(withDetach(mg), m, `Merge ${o.display_name} into ${c.display_name} and retire it`, c.display_name)}
                                  extra={picker(mg)}
                                />
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                    {c.siblings.length === 0 && !c.can_split && (
                      <p className="text-xs text-grey-400">
                        {c.split_block === 'cxa_issuer_code_not_trading_code'
                          ? 'Waits for the CXA per-fund repair: this row is still keyed on the TMX issuer code, not a trading code.'
                          : c.split_block === 'no_valid_ticker'
                            ? `No sibling row holds a link, and "${c.canonical_identifier ?? ''}" is not a ticker to split on — needs review in Cynapse.`
                            : <>No sibling row holds a link and {c.entity_type} records can&apos;t be split here — needs review in Cynapse.</>}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </div>
  );
}

function RepairRow({ title, detail, state, busy, onRun, extra }: {
  title: ReactNode;
  detail: string;
  state: ActionState | undefined;
  busy: boolean;
  onRun: (mode: 'dry' | 'apply') => void;
  extra?: ReactNode;
}) {
  const dryOk = state?.dry && !state.dry.error;
  const done = state?.applied?.applied;
  return (
    <div className="rounded-lg border border-grey-100 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-grey-800">{title}</div>
          <div className="text-xs text-grey-400">{detail}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onRun('dry')}
            disabled={busy || done}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50"
          >
            {state?.running === 'dry' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
            Dry run
          </button>
          <button
            onClick={() => onRun('apply')}
            disabled={busy || !dryOk || done}
            title={dryOk ? undefined : 'Run a dry run of this repair first'}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {state?.running === 'apply' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Apply
          </button>
        </div>
      </div>
      {state?.error && <p className="mt-2 text-xs text-red-700">{state.error}</p>}
      {state?.dry && <Outcome label="Dry run" o={state.dry} />}
      {extra}
      {state?.applied && <Outcome label="Applied" o={state.applied} />}
    </div>
  );
}

function Counts({ c }: { c?: Record<string, number> }) {
  const entries = Object.entries(c || {}).filter(([k]) => !k.startsWith('_'));
  if (entries.length === 0) return <span className="text-grey-400">nothing</span>;
  return <>{entries.map(([k, v]) => `${k} ${v}`).join(' · ')}</>;
}

const ERRORS: Record<string, string> = {
  alias_holder_is_this_company: "the record carrying this name is the issuer's own — take its link instead (below)",
  ticker_held_by_another_company: 'another company of the same type is keyed by this ticker in Cynapse — review it there',
  remove_from_required_run_a_dry_run_first: 'run a dry run first',
  merge_would_fail: 'the merge would fail — see the reason below; nothing was changed',
  cxa_issuer_code_not_trading_code: 'waits for the CXA per-fund repair (keyed on the TMX issuer code)',
  no_valid_ticker: 'the code is not a ticker',
  no_listing_evidence: 'no evidence this row is really ASX-listed (likely created from a mention) — not split',
  listed_under_another_name: 'this code is listed on ASX under another name — needs a person (wrong stored name, or a different company)',
};

function Outcome({ label, o }: { label: string; o: IssuerLinkRepairOutcome }) {
  const cyn = o.cynapse;
  const owner = (id: string) => {
    const w = o.owners?.[id];
    return w ? ` (Assayer: ${w.canonical_identifier ? `${w.canonical_identifier} · ` : ''}${w.display_name})` : ' (no Assayer row)';
  };
  return (
    <div className="mt-2 rounded bg-grey-50 px-3 py-2 text-xs text-grey-700 space-y-1">
      <div className="font-medium text-grey-800">
        {label}: {o.error ? <span className="text-red-700">{ERRORS[o.error] ?? o.error}</span> : o.applied ? 'done' : 'would apply'}
      </div>
      {o.listing_evidence && (
        <div className={o.listing_evidence.needs_review ? 'text-amber-700' : undefined}>
          Listing evidence: {o.listing_evidence.reasons.join(', ') || 'none'}
          {o.listing_evidence.needs_review && o.listing_evidence.has_evidence ? ' — name match only on a short name: check this is the listed company before applying' : ''}
          {o.listing_evidence.exchange_name ? ` — listed today as "${o.listing_evidence.exchange_name}"` : ''}
          {o.listing_evidence.probably_misbound_filings ? `; ${o.listing_evidence.probably_misbound_filings} filing(s) here may belong to that company` : ''}
        </div>
      )}
      {cyn?.holders && cyn.holders.length > 0 && cyn.holders.map(h => (
        <div key={h.entity_id}>{h.canonical_name ?? h.entity_id}{h.entity_type ? ` (${h.entity_type})` : ''}{owner(h.entity_id)}</div>
      ))}
      {o.kind === 'transfer' && o.from && (
        <div>Link {o.from.cynapse_entity_id} moves from {o.from.display_name} ({o.from.entity_type}), no merge; it keeps: <Counts c={o.from_keeps} /></div>
      )}
      {o.kind === 'rebind' && o.from && (
        <div>
          Link {o.from.cynapse_entity_id} moves from {o.from.display_name} ({o.from.entity_type});{' '}
          {o.would_move ? <>would move: <Counts c={o.would_move} /></> : <>on the sibling: <Counts c={o.sibling_references} /></>}
        </div>
      )}
      {o.would_fail && <div className="text-red-700">The merge would fail: {o.would_fail}</div>}
      {o.reset_against_old_record && (
        <div>
          Resolved against the old record {o.reset_against_old_record.old_cynapse_entity_id}, reset to resolve again:{' '}
          {o.reset_against_old_record.director_trades.length} director notice(s), {o.reset_against_old_record.holder_filings.length} holder filing(s).{' '}
          <span className="text-grey-500">{o.reset_against_old_record.cynapse_edges_not_ended}</span>
        </div>
      )}
      {o.detach && o.detach.length > 0 && (
        <div>Detached first ({o.detach.length}): {Array.from(new Set(o.detach.map(d => d.company_name))).join(', ')}</div>
      )}
      {o.unmapped && <div className="text-red-700">Not movable: <Counts c={o.unmapped} /></div>}
      {o.moved && <div>Moved: <Counts c={o.moved} /></div>}
      {cyn?.removals && Object.entries(cyn.removals).map(([id, r]) => (
        <div key={id}>
          Name comes off {r.canonical_name}{owner(id)}: {[...r.aliases_removed, ...r.entity_aliases_removed.map(a => `${a.alias_text} (${a.alias_kind})`)].join(', ') || 'nothing'}
        </div>
      ))}
      {cyn && cyn.removals && Object.keys(cyn.removals).length === 0 && <div>No record carries the name.</div>}
      {cyn?.would_create && (
        <div>Creates {cyn.would_create.name} ({cyn.would_create.entity_type}, {cyn.would_create.ticker}{cyn.would_create.exchange_code ? `, ${cyn.would_create.exchange_code}` : ''})</div>
      )}
      {cyn?.other_ticker_holders && cyn.other_ticker_holders.length > 0 && (
        <div>Ticker also held by {cyn.other_ticker_holders.map(h => `${h.canonical_name ?? h.entity_id}${h.entity_type ? ` (${h.entity_type})` : ''}`).join(', ')} — another company, not reused.</div>
      )}
      {cyn && cyn.was_created === false && cyn.entity_id && <div>Its own record already exists ({cyn.canonical_name ?? cyn.entity_id}) — it will be linked.</div>}
      {o.cynapse_entity_id && <div>Linked to {o.cynapse_entity_id}{cyn?.was_created ? ' (new record)' : ''}</div>}
      {o.resolved && <div>Parked rows re-resolved: <Counts c={o.resolved} /></div>}
    </div>
  );
}
